// ============================================
// moldClaw Billing Management Page
// ============================================

/**
 * 🧑‍💻 SWE-3: 빌링 관리 페이지
 * 🎨 CX-1: 사용자 친화적 결제 UI
 * 🎨 CX-2: 한국 SaaS UX 벤치마킹
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePayment } from '../hooks/usePayment';

// 플랜 정보
const PLAN_INFO = {
  free: { name: 'Free', price: '무료', amount: 0 },
  pro: { name: 'Pro', price: '₩19,900', amount: 19900 },
  team: { name: 'Team', price: '₩32,900', amount: 32900 },
};

export default function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    isLoading,
    error,
    billingKeys,
    subscription,
    paymentHistory,
    loadBillingKeys,
    loadSubscription,
    loadPaymentHistory,
    issueBillingKey,
    deleteBillingKey,
    setDefaultBillingKey,
    subscribe: _subscribe,
    cancelSubscription,
    isSdkLoaded,
    isMockMode,  // 💬 SWE-3: Mock 모드 상태
  } = usePayment();
  
  const [activeTab, setActiveTab] = useState<'overview' | 'payment-methods' | 'history'>('overview');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // 데이터 로드
  useEffect(() => {
    if (user) {
      loadBillingKeys();
      loadSubscription();
      loadPaymentHistory();
    }
  }, [user, loadBillingKeys, loadSubscription, loadPaymentHistory]);
  
  // 에러 토스트
  useEffect(() => {
    if (error) {
      // TODO: 토스트 컴포넌트 사용
      console.error(error);
    }
  }, [error]);
  
  // ============================================
  // 핸들러
  // ============================================
  
  const handleAddPaymentMethod = async () => {
    setActionLoading('add-card');
    const result = await issueBillingKey();
    if (result) {
      // 성공 토스트
      console.log('결제 수단이 등록되었습니다');
    }
    setActionLoading(null);
  };
  
  const handleDeletePaymentMethod = async (id: string) => {
    if (!confirm('이 결제 수단을 삭제하시겠습니까?')) return;
    
    setActionLoading(`delete-${id}`);
    const success = await deleteBillingKey(id);
    if (success) {
      console.log('결제 수단이 삭제되었습니다');
    }
    setActionLoading(null);
  };
  
  const handleSetDefaultPaymentMethod = async (id: string) => {
    setActionLoading(`default-${id}`);
    const success = await setDefaultBillingKey(id);
    if (success) {
      console.log('기본 결제 수단이 변경되었습니다');
    }
    setActionLoading(null);
  };
  
  const handleCancelSubscription = async () => {
    setActionLoading('cancel');
    const success = await cancelSubscription(cancelReason);
    if (success) {
      setShowCancelModal(false);
      setCancelReason('');
      console.log('구독이 취소 예약되었습니다');
    }
    setActionLoading(null);
  };
  
  const handleUpgrade = (tier: 'pro' | 'team') => {
    // 결제 수단 없으면 먼저 등록
    if (billingKeys.length === 0) {
      navigate('/pricing', { state: { targetTier: tier, needsPaymentMethod: true } });
    } else {
      navigate('/pricing', { state: { targetTier: tier } });
    }
  };
  
  // ============================================
  // 렌더링
  // ============================================
  
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>로그인이 필요합니다</p>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* 💬 SWE-3: Mock 모드 배너 */}
        {isMockMode && (
          <div className="mb-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-center gap-3">
            <span className="text-2xl">🧪</span>
            <div>
              <p className="text-yellow-400 font-medium">테스트 모드</p>
              <p className="text-yellow-400/70 text-sm">
                Mock 결제 모드가 활성화되어 있습니다. 실제 결제가 이루어지지 않습니다.
              </p>
            </div>
          </div>
        )}
        
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">결제 관리</h1>
          <p className="mt-2 text-gray-400">구독 및 결제 수단을 관리하세요</p>
        </div>
        
        {/* 탭 */}
        <div className="flex space-x-4 border-b border-gray-700 mb-8">
          <TabButton
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
          >
            구독 현황
          </TabButton>
          <TabButton
            active={activeTab === 'payment-methods'}
            onClick={() => setActiveTab('payment-methods')}
          >
            결제 수단
          </TabButton>
          <TabButton
            active={activeTab === 'history'}
            onClick={() => setActiveTab('history')}
          >
            결제 내역
          </TabButton>
        </div>
        
        {/* 콘텐츠 */}
        {activeTab === 'overview' && (
          <SubscriptionOverview
            subscription={subscription}
            onUpgrade={handleUpgrade}
            onCancel={() => setShowCancelModal(true)}
            isLoading={isLoading}
          />
        )}
        
        {activeTab === 'payment-methods' && (
          <PaymentMethods
            billingKeys={billingKeys}
            onAdd={handleAddPaymentMethod}
            onDelete={handleDeletePaymentMethod}
            onSetDefault={handleSetDefaultPaymentMethod}
            isLoading={isLoading}
            actionLoading={actionLoading}
            isSdkLoaded={isSdkLoaded}
          />
        )}
        
        {activeTab === 'history' && (
          <PaymentHistory
            payments={paymentHistory}
            isLoading={isLoading}
          />
        )}
        
        {/* 구독 취소 모달 */}
        {showCancelModal && (
          <CancelSubscriptionModal
            onConfirm={handleCancelSubscription}
            onClose={() => setShowCancelModal(false)}
            reason={cancelReason}
            setReason={setCancelReason}
            isLoading={actionLoading === 'cancel'}
            periodEnd={subscription?.currentPeriodEnd}
          />
        )}
      </div>
    </div>
  );
}

// ============================================
// Sub Components
// ============================================

function TabButton({ active, onClick, children }: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`pb-4 px-2 text-sm font-medium transition-colors ${
        active
          ? 'text-space-blue-light border-b-2 border-space-blue-light'
          : 'text-gray-400 hover:text-gray-300'
      }`}
    >
      {children}
    </button>
  );
}

function SubscriptionOverview({
  subscription,
  onUpgrade,
  onCancel,
  isLoading,
}: {
  subscription: any;
  onUpgrade: (tier: 'pro' | 'team') => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  
  const currentTier = subscription?.tier || 'free';
  const planInfo = PLAN_INFO[currentTier as keyof typeof PLAN_INFO];
  
  return (
    <div className="space-y-6">
      {/* 현재 플랜 */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">현재 플랜</h3>
            <p className="text-3xl font-bold text-white mt-2">
              {planInfo.name}
              <span className="text-lg text-gray-400 ml-2">{planInfo.price}/월</span>
            </p>
          </div>
          
          {subscription?.status === 'active' && (
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
              활성
            </span>
          )}
          
          {subscription?.cancelAtPeriodEnd && (
            <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
              취소 예정
            </span>
          )}
        </div>
        
        {subscription && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">다음 결제일</p>
                <p className="text-white">
                  {subscription.cancelAtPeriodEnd
                    ? '취소됨'
                    : formatDate(subscription.nextBillingDate)}
                </p>
              </div>
              <div>
                <p className="text-gray-400">구독 시작일</p>
                <p className="text-white">{formatDate(subscription.currentPeriodStart)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 플랜 변경 */}
      {currentTier !== 'team' && (
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">플랜 업그레이드</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            {currentTier === 'free' && (
              <button
                onClick={() => onUpgrade('pro')}
                className="flex-1 py-3 px-4 bg-space-blue hover:bg-space-blue hover:brightness-110 text-white rounded-lg font-medium transition-colors"
              >
                Pro로 업그레이드 (₩19,900/월)
              </button>
            )}
            <button
              onClick={() => onUpgrade('team')}
              className="flex-1 py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
            >
              Team으로 업그레이드 (₩32,900/월)
            </button>
          </div>
        </div>
      )}
      
      {/* 구독 취소 */}
      {subscription && subscription.status === 'active' && !subscription.cancelAtPeriodEnd && (
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">구독 취소</h3>
          <p className="text-gray-400 text-sm mb-4">
            구독을 취소해도 현재 결제 기간이 끝날 때까지 서비스를 이용할 수 있습니다.
          </p>
          <button
            onClick={onCancel}
            className="py-2 px-4 border border-red-500 text-red-400 hover:bg-red-500/10 rounded-lg font-medium transition-colors"
          >
            구독 취소
          </button>
        </div>
      )}
    </div>
  );
}

function PaymentMethods({
  billingKeys,
  onAdd,
  onDelete,
  onSetDefault,
  isLoading,
  actionLoading,
  isSdkLoaded,
}: {
  billingKeys: any[];
  onAdd: () => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
  isLoading: boolean;
  actionLoading: string | null;
  isSdkLoaded: boolean;
}) {
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  
  return (
    <div className="space-y-6">
      {/* 등록된 카드 목록 */}
      {billingKeys.length > 0 ? (
        <div className="space-y-4">
          {billingKeys.map((card) => (
            <div
              key={card.id}
              className="bg-gray-800 rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-8 bg-gradient-to-r from-space-blue to-purple-500 rounded flex items-center justify-center">
                  <CreditCardIcon />
                </div>
                <div>
                  <p className="text-white font-medium">
                    {card.cardCompany || '카드'} {card.cardNumberMasked}
                  </p>
                  <p className="text-sm text-gray-400">
                    {card.cardType === 'CREDIT' ? '신용카드' : '체크카드'}
                    {card.isDefault && (
                      <span className="ml-2 text-space-blue-light">기본 결제 수단</span>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {!card.isDefault && (
                  <button
                    onClick={() => onSetDefault(card.id)}
                    disabled={actionLoading === `default-${card.id}`}
                    className="text-sm text-gray-400 hover:text-white px-3 py-1 transition-colors"
                  >
                    {actionLoading === `default-${card.id}` ? '처리 중...' : '기본으로 설정'}
                  </button>
                )}
                <button
                  onClick={() => onDelete(card.id)}
                  disabled={actionLoading === `delete-${card.id}`}
                  className="text-sm text-red-400 hover:text-red-300 px-3 py-1 transition-colors"
                >
                  {actionLoading === `delete-${card.id}` ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl p-8 text-center">
          <p className="text-gray-400 mb-4">등록된 결제 수단이 없습니다</p>
        </div>
      )}
      
      {/* 카드 추가 버튼 */}
      <button
        onClick={onAdd}
        disabled={!isSdkLoaded || actionLoading === 'add-card'}
        className="w-full py-3 px-4 border-2 border-dashed border-gray-600 hover:border-gray-500 text-gray-400 hover:text-gray-300 rounded-xl font-medium transition-colors disabled:opacity-50"
      >
        {actionLoading === 'add-card' ? (
          '처리 중...'
        ) : !isSdkLoaded ? (
          '결제 SDK 로딩 중...'
        ) : (
          '+ 새 결제 수단 추가'
        )}
      </button>
    </div>
  );
}

function PaymentHistory({ payments, isLoading }: {
  payments: any[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  
  if (payments.length === 0) {
    return (
      <div className="bg-gray-800 rounded-xl p-8 text-center">
        <p className="text-gray-400">결제 내역이 없습니다</p>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-800 rounded-xl overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-700">
            <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">날짜</th>
            <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">상품</th>
            <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">금액</th>
            <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">상태</th>
            <th className="text-left text-gray-400 text-sm font-medium px-6 py-4"></th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <tr key={payment.id} className="border-b border-gray-700/50 last:border-0">
              <td className="px-6 py-4 text-white text-sm">
                {formatDate(payment.paidAt || payment.createdAt)}
              </td>
              <td className="px-6 py-4 text-white text-sm">{payment.productName}</td>
              <td className="px-6 py-4 text-white text-sm">
                ₩{payment.amount.toLocaleString()}
              </td>
              <td className="px-6 py-4">
                <StatusBadge status={payment.status} />
              </td>
              <td className="px-6 py-4">
                {payment.receiptUrl && (
                  <a
                    href={payment.receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-space-blue-light hover:text-space-blue"
                  >
                    영수증
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CancelSubscriptionModal({
  onConfirm,
  onClose,
  reason,
  setReason,
  isLoading,
  periodEnd,
}: {
  onConfirm: () => void;
  onClose: () => void;
  reason: string;
  setReason: (r: string) => void;
  isLoading: boolean;
  periodEnd?: string;
}) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-white mb-4">구독을 취소하시겠어요?</h3>
        
        <p className="text-gray-400 mb-4">
          구독을 취소해도 <span className="text-white font-medium">{formatDate(periodEnd)}</span>까지는
          Pro 기능을 계속 이용할 수 있어요.
        </p>
        
        <div className="mb-6">
          <label className="block text-sm text-gray-400 mb-2">
            취소 사유를 알려주시면 서비스 개선에 도움이 돼요 (선택)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="취소 사유를 입력해주세요"
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-space-blue"
            rows={3}
          />
        </div>
        
        <div className="flex space-x-4">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
          >
            돌아가기
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? '처리 중...' : '구독 취소'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles = {
    paid: 'bg-green-500/20 text-green-400',
    pending: 'bg-yellow-500/20 text-yellow-400',
    failed: 'bg-red-500/20 text-red-400',
    cancelled: 'bg-gray-500/20 text-gray-400',
    refunded: 'bg-blue-500/20 text-blue-400',
  };
  
  const labels = {
    paid: '결제 완료',
    pending: '처리 중',
    failed: '결제 실패',
    cancelled: '취소됨',
    refunded: '환불됨',
  };
  
  return (
    <span className={`px-2 py-1 rounded text-xs ${styles[status as keyof typeof styles] || styles.pending}`}>
      {labels[status as keyof typeof labels] || status}
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-gray-800 rounded-xl p-6 animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-gray-700 rounded w-1/2"></div>
        </div>
      ))}
    </div>
  );
}

function CreditCardIcon() {
  return (
    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}

function formatDate(dateString?: string): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
